// Armes du prototype. Le recul suit un motif fixe (en degrés, décalage latéral de la visée tir après tir) :
// il s'apprend et se compense à la souris. Seul le tir en mouvement ajoute une part de hasard.

import type { Caliber } from './items';

export type WeaponId = 'pistol' | 'ar' | 'dmr' | 'sg' | 'smg' | 'ak' | 'bolt';
// Emplacements d'accessoires : viseur, bouche du canon, chargeur, poignée
export type ModSlot = 'optic' | 'muzzle' | 'mag' | 'grip';

export interface Weapon {
  id: WeaponId;
  name: string;
  short: string;
  auto: boolean;
  rpm: number;
  damage: number;
  magazine: number;
  reload: number; // s
  range: number; // m, pleine puissance jusqu'ici
  falloff: number; // part des dégâts restante à 2 × range
  pattern: number[]; // degrés
  recover: number; // s sans tirer pour revenir au début du motif
  moveSpread: number; // degrés, demi-angle de dispersion en déplacement
  noise: number; // m
  mesh: string; // maille d'arme dans le modèle de personnage
  caliber: Caliber;
  pellets?: number; // fusil à pompe : plombs en éventail fixe
  fan?: number; // demi-angle de l'éventail (degrés)
  slots: ModSlot[]; // accessoires possibles
  reach?: number; // portée de visée au clic droit (1 = normale ; lunette intégrée : plus loin)
}

export const WEAPONS: Record<WeaponId, Weapon> = {
  pistol: {
    id: 'pistol', name: 'Pistolet 9 mm', short: '9 mm', auto: false, rpm: 400, damage: 28, magazine: 15, reload: 1.5,
    range: 30, falloff: 0.6, pattern: [0, 0.5, -0.3, 0.8, -0.6, 1.0, -0.9, 1.2], recover: 0.28, moveSpread: 3, noise: 70, mesh: 'Pistol', caliber: '9', slots: ['optic', 'muzzle', 'mag'],
  },
  ar: {
    id: 'ar', name: 'Fusil d’assaut 5,56', short: '5,56', auto: true, rpm: 620, damage: 24, magazine: 30, reload: 2.3,
    range: 55, falloff: 0.7,
    pattern: [0, 0.3, 0.8, 1.3, 1.6, 1.4, 0.6, -0.4, -1.4, -2.2, -2.6, -2.4, -1.6, -0.6, 0.6, 1.8, 2.6, 3.0, 2.6, 1.6, 0.4, -0.8, -2.0, -2.8, -3.0, -2.4, -1.2, 0.2, 1.4, 2.4],
    recover: 0.32, moveSpread: 4.5, noise: 120, mesh: 'SMG', caliber: '556', slots: ['optic', 'muzzle', 'mag', 'grip'],
  },
  dmr: {
    id: 'dmr', name: 'Fusil à lunette', short: '7,62', auto: false, rpm: 160, damage: 58, magazine: 10, reload: 2.6,
    range: 90, falloff: 0.75, pattern: [0, 1.2, -0.8, 1.6, -1.2, 1.8], recover: 0.45, moveSpread: 7, noise: 120, mesh: 'Rifle', caliber: '762', slots: ['muzzle', 'mag', 'grip'], reach: 1.6,
  },
  sg: {
    id: 'sg', name: 'Fusil à pompe cal. 12', short: 'cal. 12', auto: false, rpm: 70, damage: 14, magazine: 6, reload: 3.2,
    range: 12, falloff: 0.3, pattern: [0, 1.5, -1.5, 2], recover: 1.1, moveSpread: 3, noise: 110, mesh: 'Shotgun', caliber: '12', pellets: 8, fan: 5, slots: ['optic', 'grip'],
  },
  smg: {
    id: 'smg', name: 'Pistolet-mitrailleur 9 mm', short: 'PM 9 mm', auto: true, rpm: 850, damage: 19, magazine: 30, reload: 2.0,
    range: 28, falloff: 0.55, pattern: [0, 0.3, 0.6, 0.4, -0.2, -0.7, -1.0, -0.8, -0.2, 0.5, 1.0, 1.2, 0.8, 0.1, -0.6, -1.1, -1.3, -0.9, -0.2, 0.6, 1.1, 1.3, 0.9, 0.2, -0.5, -1.0, -1.2, -0.8, -0.1, 0.6],
    recover: 0.26, moveSpread: 2.5, noise: 95, mesh: 'SMG', caliber: '9', slots: ['optic', 'muzzle', 'mag', 'grip'],
  },
  ak: {
    id: 'ak', name: 'Fusil d’assaut 7,62', short: 'FA 7,62', auto: true, rpm: 560, damage: 31, magazine: 30, reload: 2.6,
    range: 50, falloff: 0.7,
    pattern: [0, 0.6, 1.4, 2.2, 2.6, 2.2, 1.0, -0.6, -2.0, -3.2, -3.6, -3.2, -2.0, -0.4, 1.2, 2.6, 3.6, 4.0, 3.4, 2.0, 0.4, -1.4, -3.0, -4.0, -4.2, -3.4, -1.8, 0.2, 2.0, 3.4],
    recover: 0.36, moveSpread: 5.5, noise: 130, mesh: 'SMG', caliber: '762', slots: ['optic', 'muzzle', 'mag', 'grip'],
  },
  bolt: {
    id: 'bolt', name: 'Fusil à verrou 7,62', short: 'Verrou', auto: false, rpm: 45, damage: 92, magazine: 5, reload: 3.0,
    range: 110, falloff: 0.85, pattern: [0, 2.4, -2.0, 2.6], recover: 1.4, moveSpread: 9, noise: 140, mesh: 'Rifle', caliber: '762', slots: ['optic', 'muzzle'], reach: 1.2,
  },
};

// Ordre fixe (indice transmis dans les instantanés)
export const WEAPON_ORDER: WeaponId[] = ['ar', 'dmr', 'pistol', 'sg', 'smg', 'ak', 'bolt'];

// Décalages (degrés) des plombs d'un tir de fusil à pompe : un éventail régulier, pas de hasard
export function pelletOffsets(w: Weapon): number[] {
  const n = w.pellets ?? 1, f = w.fan ?? 0;
  if (n === 1) return [0];
  return Array.from({ length: n }, (_, i) => -f + (2 * f * i) / (n - 1));
}

// Décalage de recul (degrés) du n-ième tir d'une rafale ; au-delà du motif, on reprend sa seconde moitié
export function recoilOffset(w: Weapon, shotIndex: number, aiming: boolean): number {
  const p = w.pattern;
  const half = Math.floor(p.length / 2);
  const i = shotIndex < p.length ? shotIndex : half + ((shotIndex - p.length) % (p.length - half));
  return p[i]! * (aiming ? 0.6 : 1) * ((w as WeaponStats).recoilMul ?? 1);
}

export function damageAt(w: Weapon, dist: number): number {
  if (dist <= w.range) return w.damage;
  const f = Math.min(1, (dist - w.range) / w.range);
  return w.damage * (1 - f * (1 - w.falloff));
}

export const shotInterval = (w: Weapon) => 60 / w.rpm;

// ---------------------------------------------------------------- Accessoires

// Chaque accessoire multiplie certaines caractéristiques (1 = sans effet). mag : chargeur × ce facteur.
export interface ModDef {
  id: string; slot: ModSlot; name: string;
  only?: WeaponId[]; // armes compatibles (toutes celles qui ont l'emplacement, sinon)
  recoil?: number; move?: number; mag?: number; reload?: number; noise?: number; damage?: number; range?: number; reach?: number;
  suppressed?: boolean;
}
export const MODS: Record<string, ModDef> = {
  o_reddot: { id: 'o_reddot', slot: 'optic', name: 'Viseur point rouge', recoil: 0.92, reach: 1.15 },
  o_holo: { id: 'o_holo', slot: 'optic', name: 'Viseur holographique', only: ['ar', 'ak', 'smg', 'sg'], reach: 1.25, move: 0.92 },
  o_x4: { id: 'o_x4', slot: 'optic', name: 'Lunette × 4', only: ['ar', 'ak', 'bolt'], reach: 1.9, move: 1.2, recoil: 0.95 },
  mz_supp: { id: 'mz_supp', slot: 'muzzle', name: 'Silencieux', noise: 0.35, damage: 0.95, range: 0.95, suppressed: true },
  mz_comp: { id: 'mz_comp', slot: 'muzzle', name: 'Compensateur', only: ['ar', 'ak', 'smg', 'pistol'], recoil: 0.72, noise: 1.15 },
  mz_brake: { id: 'mz_brake', slot: 'muzzle', name: 'Frein de bouche', only: ['dmr', 'bolt', 'ak'], recoil: 0.78, noise: 1.2 },
  mg_ext: { id: 'mg_ext', slot: 'mag', name: 'Chargeur étendu', mag: 1.5, reload: 1.12 },
  mg_drum: { id: 'mg_drum', slot: 'mag', name: 'Chargeur tambour', only: ['ar', 'ak', 'smg'], mag: 2, reload: 1.45, move: 1.15 },
  gp_vert: { id: 'gp_vert', slot: 'grip', name: 'Poignée verticale', recoil: 0.85, move: 0.9 },
  gp_angle: { id: 'gp_angle', slot: 'grip', name: 'Poignée d’angle', recoil: 0.95, move: 0.75 },
};
export type Mods = Partial<Record<ModSlot, string>>;
export const SLOT_NAMES: Record<ModSlot, string> = { optic: 'Viseur', muzzle: 'Bouche', mag: 'Chargeur', grip: 'Poignée' };

export const modFits = (w: WeaponId, modId: string) => {
  const m = MODS[modId];
  return !!m && WEAPONS[w].slots.includes(m.slot) && (!m.only || m.only.includes(w));
};

// Caractéristiques réelles d'une arme avec ses accessoires (mises en cache : appelées à chaque tir)
export interface WeaponStats extends Weapon { recoilMul: number; reach: number; suppressed: boolean }
const statCache = new Map<string, WeaponStats>();
export const modsKey = (m?: Mods) => (m ? Object.values(m).filter(Boolean).sort().join(',') : '');
export function weaponStats(id: WeaponId, mods?: Mods): WeaponStats {
  const key = `${id}|${modsKey(mods)}`;
  const hit = statCache.get(key);
  if (hit) return hit;
  const b = WEAPONS[id];
  const s: WeaponStats = { ...b, recoilMul: 1, reach: b.reach ?? 1, suppressed: false };
  for (const mid of Object.values(mods ?? {})) {
    if (!mid || !modFits(id, mid)) continue;
    const m = MODS[mid]!;
    s.recoilMul *= m.recoil ?? 1;
    s.moveSpread *= m.move ?? 1;
    s.magazine = Math.round(s.magazine * (m.mag ?? 1));
    s.reload *= m.reload ?? 1;
    s.noise *= m.noise ?? 1;
    s.damage *= m.damage ?? 1;
    s.range *= m.range ?? 1;
    s.reach = Math.max(s.reach, (b.reach ?? 1) * (m.reach ?? 1));
    if (m.suppressed) s.suppressed = true;
  }
  statCache.set(key, s);
  return s;
}
